import apiClient from '../api/client';

export interface ProcessingConfig {
  prompt_id?: string;        // ID of the Harmony/Symphony prompt to use
  prompt_text?: string;      // The actual prompt text to use (avoids API call to admin service)
  chunk_size?: number;       // Size of text chunks (default: 2000)
  chunk_overlap?: number;    // Overlap between chunks (default: 200)
  extraction_model?: string; // LLM model (default: "claude-3-5-sonnet-20241022")
  document_type?: string;    // Document type (default: "general")
  system_identifier?: string; // System identifier (harmony or symphony)
}

export interface Document {
  id: string;
  fileName?: string;
  file_name?: string;
  originalName?: string;
  original_name?: string;
  mimeType?: string;
  mime_type?: string;
  size: number;
  checksum: string;
  storagePath?: string;
  storage_path?: string;
  uploadedBy?: string;
  uploaded_by?: string;
  uploadedByEmail?: string;
  uploaded_by_email?: string;
  metadata?: Record<string, any>;
  createdAt?: string;
  created_at?: string;
  uploadedAt?: string;
  uploaded_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

export interface DocumentListResponse {
  documents: Document[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

class DocumentService {

  async uploadDocuments(
    files: File[],
    onProgress?: (progress: UploadProgress[]) => void,
    processingConfig?: ProcessingConfig
  ): Promise<Document[]> {
    const formData = new FormData();
    const progressMap = new Map<string, UploadProgress>();

    // Initialize progress tracking
    // Document service expects 'file' field for single upload
    // We'll send them one by one for now
    if (files.length === 1) {
      formData.append('file', files[0]);
      progressMap.set(files[0].name, {
        fileName: files[0].name,
        progress: 0,
        status: 'pending',
      });
    } else {
      // For multiple files, use 'files' field for batch endpoint
      files.forEach(file => {
        formData.append('files', file);
        progressMap.set(file.name, {
          fileName: file.name,
          progress: 0,
          status: 'pending',
        });
      });
    }

    // Add processing config if provided
    if (processingConfig) {
      // Extract system_identifier to send as separate form field
      const { system_identifier, ...configWithoutSystemId } = processingConfig;

      // Add system_identifier as separate field (backend expects this)
      if (system_identifier) {
        formData.append('system_identifier', system_identifier);
      }

      // Add remaining processing config as JSON
      if (Object.keys(configWithoutSystemId).length > 0) {
        formData.append('processing_config', JSON.stringify(configWithoutSystemId));
      }
    }

    if (onProgress) {
      onProgress(Array.from(progressMap.values()));
    }

    try {
      // Use different endpoints for single vs batch upload
      const endpoint = files.length === 1 ? '/documents' : '/documents/batch';
      const response = await apiClient.post(
        endpoint,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;

            // Update all file progress uniformly (since it's a single request)
            files.forEach(file => {
              progressMap.set(file.name, {
                fileName: file.name,
                progress: percentCompleted,
                status: percentCompleted === 100 ? 'completed' : 'uploading',
              });
            });

            if (onProgress) {
              onProgress(Array.from(progressMap.values()));
            }
          },
        }
      );

      return response.documents || response;
    } catch (error: any) {
      // Mark all files as error
      files.forEach(file => {
        progressMap.set(file.name, {
          fileName: file.name,
          progress: 0,
          status: 'error',
          error: error.response?.data?.error?.message || 'Upload failed',
        });
      });

      if (onProgress) {
        onProgress(Array.from(progressMap.values()));
      }

      throw error;
    }
  }

  async getDocuments(page = 1, limit = 20, systemIdentifier?: string): Promise<DocumentListResponse> {
    try {
      // apiClient.get() extracts response.data.data and returns it directly
      // So the result is already the documents array
      const params: any = {
        page,
        limit,
      };

      if (systemIdentifier) {
        params.system_identifier = systemIdentifier;
      }

      const documents = await apiClient.get<Document[]>(`/documents`, {
        params,
      });

      // Return the documents with basic pagination
      // Since we can't access meta through apiClient.get, we'll use array length
      return {
        documents: documents || [],
        pagination: {
          page: page,
          limit: limit,
          total: documents?.length || 0,
          totalPages: 1,
        }
      };
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      throw new Error(
        error.response?.data?.error?.message || 'Failed to fetch documents'
      );
    }
  }

  async getDocument(id: string): Promise<Document> {
    try {
      const response = await apiClient.get(`/documents/${id}`);

      return response.document || response;
    } catch (error: any) {
      console.error('Error fetching document:', error);
      throw new Error(
        error.response?.data?.error?.message || 'Failed to fetch document'
      );
    }
  }

  async deleteDocument(id: string): Promise<void> {
    try {
      await apiClient.delete(`/documents/${id}`);
    } catch (error: any) {
      console.error('Error deleting document:', error);
      throw new Error(
        error.response?.data?.error?.message || 'Failed to delete document'
      );
    }
  }

  async downloadDocument(id: string): Promise<Blob> {
    try {
      const response = await apiClient.get(`/documents/${id}/download`, {
        responseType: 'blob',
      });

      return response;
    } catch (error: any) {
      console.error('Error downloading document:', error);
      throw new Error(
        error.response?.data?.error?.message || 'Failed to download document'
      );
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  getMimeTypeIcon(mimeType: string): string {
    if (!mimeType) return '📄';

    const typeMap: Record<string, string> = {
      'application/pdf': '📕',
      'application/msword': '📘',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📘',
      'application/vnd.ms-excel': '📊',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
      'application/vnd.ms-powerpoint': '📙',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '📙',
      'text/plain': '📝',
      'text/csv': '📊',
      'application/json': '📋',
      'application/xml': '📋',
      'text/xml': '📋',
      'application/zip': '📦',
      'application/x-zip-compressed': '📦',
      'application/x-rar-compressed': '📦',
    };

    // Check for image types
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎬';
    if (mimeType.startsWith('audio/')) return '🎵';

    return typeMap[mimeType] || '📄';
  }
}

export default new DocumentService();